import React, { useState, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';
import {
  Alert, Button, Heading, Link,
} from '@printdeal/ui-library';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { hasActiveArtworkServiceOfTypes, SERVICES } from '@printdeal/business-logic';
import { ServiceFactory } from '../../../../services/ServiceFactory';
import { useSanitizedOrder } from '../hooks/useOrders';
import { Environment } from '../../../../helpers/env';

const STATUS = {
  INITIAL: 'initial',
  UPLOAD_PRESENT: 'upload-present',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
};

interface AlternativeUploadFormProps {
  orderId: string;
  lineItemId: string;
  onSubmit: () => void;
  editorType: string;
}

const AlternativeUploadForm = ({
  orderId,
  lineItemId,
  onSubmit,
  editorType,
}: AlternativeUploadFormProps) => {
  const [status, setStatus] = useState(STATUS.INITIAL);
  const [files, setFiles] = useState<IAlternativeUpload[]>([]);
  const location = useLocation();

  const intl = useIntl();
  const { order } = useSanitizedOrder(orderId, intl.locale, 'order_detail_api_failure');

  const isFileCheckServiceSelected = order && hasActiveArtworkServiceOfTypes(
    lineItemId,
    order,
    [SERVICES.FILE_CHECK],
    Environment,
  );

  const addUploadedFileToLineItem = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(STATUS.LOADING);

    try {
      await ServiceFactory.createFileDeliveryService().addFileUrlsToLineItem(
        orderId,
        lineItemId,
        files,
        editorType,
      );
      addDataLayerEntry('alternative_upload', {
        action: 'add_uploaded_files_to_order',
        extraData: isFileCheckServiceSelected ? 'with_fileCheck' : 'without_fileCheck',
      });
      onSubmit();
    } catch (error) {
      addDataLayerEntry('alternative_upload', {
        action: 'error_while_adding_uploaded_files_to_order',
        extraData: isFileCheckServiceSelected ? 'with_fileCheck' : 'without_fileCheck',
      });
      setStatus(STATUS.ERROR);
    }
  };

  const isDirectUpload = editorType === 'direct-upload';
  const messages = defineMessages({
    directUploadTitle: {
      id: 'account.lineItem.alternativeUpload.directUplod.title',
      defaultMessage: 'Upload file',
    },
    alternativeUploadMultipleFilesTitle: {
      id: 'account.lineItem.alternativeUpload.title',
      defaultMessage: 'Alternative upload title',
    },
    directUploadButton: {
      id: 'account.lineItem.alternativeUpload.directUpload.submitButton',
      defaultMessage: 'Upload file',
    },
    alternativeUploadMultipleFilesButton: {
      id: 'account.lineItem.alternativeUpload.doUpload',
      defaultMessage: 'Alternative file upload to line item',
    },
  });

  useEffect(() => {
    if (files.length > 0 && files.every((file: IAlternativeUpload) => file.uploadTimestamp)) {
      return setStatus(STATUS.UPLOAD_PRESENT);
    }
    return setStatus(STATUS.INITIAL);
  }, [files]);

  return (
    <form onSubmit={(event) => addUploadedFileToLineItem(event)} method="post" className="pd-mb-6">
      {status === STATUS.ERROR && (
        <Alert className="pd-mb-3" variant="error">
          <FormattedMessage
            id="account.alternativeUpload.deliveryError"
            defaultMessage="Oops! Something went wrong trying to attach your file to your lineItem"
          />
        </Alert>
      )}
      <Heading className="pd-gap-3">
        {isDirectUpload
          ? intl.formatMessage(messages.directUploadTitle)
          : intl.formatMessage(messages.alternativeUploadMultipleFilesTitle)}
      </Heading>
      <AlternativeUpload
        id="my-account"
        name="my-account"
        onChange={(uploads: IAlternativeUpload[]) => setFiles(uploads)}
        className="pd-mb-5 pd-mt-3"
        dataLayerData={{ location: location.href }}
        accept={isDirectUpload ? 'application/pdf' : undefined}
        maxFiles={isDirectUpload ? 1 : undefined}
      />

      {isFileCheckServiceSelected && (
        <Alert variant="info">
          <FormattedMessage
            id="account.alternativeUpload.with.fileCheck.deliveryExplanation"
            defaultMessage="You have chosen for file control by a specialist. Our experienced colleagues check all printing technical aspects that are important for your product."
          />
        </Alert>
      )}
      {isDirectUpload && (
        <Alert variant="info">
          <FormattedMessage
            id="account.alternativeUpload.without.fileCheck.deliveryExplanation"
            defaultMessage="You have chosen automatic file checking. Our system automatically checks your print-ready PDF file for a number of relevant printing technical aspects."
          />
        </Alert>
      )}
      {editorType === 'alternative-upload' && !isFileCheckServiceSelected && (
      <Alert variant="warning">
        <FormattedMessage
          id="account.alternativeUpload.deliveryExplanation"
          defaultMessage="Because manually reviewing your artwork takes time, it can happen that your file is delivered after the delivery deadline. Keep in mind that your order can arrive a day late."
        />
      </Alert>
      )}
      <div className="pd-mt-5 pd-flex pd-items-center pd-justify-between">
        <Link
          as={GatsbyLink}
          to="../.."
          variant="secondary"
          iconBefore="chevron-left"
        >
          <FormattedMessage
            id="account.alternativeUpload.backToOrder"
            defaultMessage="Back to order"
          />
        </Link>
        <Button
          type="submit"
          disabled={[STATUS.INITIAL, STATUS.LOADING, STATUS.ERROR].includes(
            status,
          )}
          isLoading={status === STATUS.LOADING}
        >
          {isDirectUpload
            ? intl.formatMessage(messages.directUploadButton)
            : intl.formatMessage(messages.alternativeUploadMultipleFilesButton)}
        </Button>
      </div>
    </form>
  );
};

export default AlternativeUploadForm;
